import { useMemo } from "react";
import type { HydratedAsset } from "./useFolderAssets";
import { trpc } from "../trpc";

/**
 * Lists every asset under each folder id and its descendants in the current
 * account, then returns a deduplicated union by asset id.
 */
export function useListAssetsInFolderSubtrees(
  folderIds: string[] | undefined,
  options?: { ownerId?: string },
) {
  const normalizedFolderIds = useMemo(
    () => [...new Set((folderIds ?? []).filter((id) => id.length > 0))],
    [folderIds],
  );

  const queries = trpc.useQueries((t) =>
    normalizedFolderIds.map((folderId) =>
      t.folder.listAssetsInFolderSubtree(
        {
          folderId,
          ...(options?.ownerId ? { ownerId: options.ownerId } : {}),
        },
        {
          enabled: normalizedFolderIds.length > 0,
          staleTime: 1000 * 60 * 5,
        },
      ),
    ),
  );

  const resultsKey = queries.map((query) => query.dataUpdatedAt).join(",");

  const assets = useMemo(() => {
    const byId = new Map<string, HydratedAsset>();
    for (const query of queries) {
      for (const asset of query.data ?? []) {
        byId.set(asset.id, asset);
      }
    }
    return [...byId.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queries is a new ref each render; recompute when query data changes
  }, [resultsKey]);

  return {
    assets,
    isLoading: queries.some((query) => query.isLoading),
    error: queries.find((query) => query.error)?.error,
    refetchAll: async () => {
      await Promise.all(queries.map((query) => query.refetch()));
    },
  };
}
